<script setup lang="ts">
import router from '@/router';
import ModifyLinkService from '@/services/ModifyLinkService.js';
import SendedFormPopUp from '@/components/modify/SendedFormPopUp.vue';
import SendingFormPopUp from '@/components/modify/SendingFormPopUp.vue';
import { ref, toRef } from 'vue';
import { useToast } from 'vue-toastification';
import { useModifyLinkStore } from '@/stores/modifyLink/modifyLink.store';
import InpostGeowidgetElement from '@/components/modify/InpostGeowidgetElement.vue';
import { Logger } from '@/middlewares/sentry';
import { IProductBundleState } from 'interface/db/userProductBundle';
import GoogleService from '@/services/GoogleService';

const toast = useToast();
const googleDriveDirectory = import.meta.env.VITE_GOOGLE_DRIVE_DIRECTORY ?? '';

const modifyStore = useModifyLinkStore();

const dateRef = ref<HTMLInputElement | null>(null);
const herNameRef = ref<HTMLInputElement | null>(null);
const hisNameRef = ref<HTMLInputElement | null>(null);

const date = ref<string | null>(null);
const herName = ref<string | null>(null);
const hisName = ref<string | null>(null);
const isGallery = ref<boolean>(false);
const paczkomatAddress = ref<string>('');

const fileRef = ref<HTMLInputElement | null>(null);
const fileToUpload = ref<File | null>(null);

function supportedImage(file) {
  return /^image\//.test(file.type) && !/.heic$/.test(file.name);
}

function supportedFile(file) {
  return /.heic$/.test(file.name) || supportedImage(file);
}

async function previewFile() {
  for (const file of fileRef.value?.files ?? []) {
    const maxSize = 26214400; // 25 MB
    if (supportedFile(file) && file.size < maxSize) {
      fileToUpload.value = file;
    } else {
      toast.error('Wybrano nieodpowiedni plik');
    }
  }
}

const isBundleToConnect = toRef(
  () => modifyStore.getLinkData.isBundleToConnect,
);

const isFormSending = ref<boolean>(false);
const isFormSended = ref<boolean>(false);

async function submitTokenDateForm() {
  if (date.value === null) {
    toast.error('Nie wypełniono daty');
    dateRef.value?.focus();
    return;
  }
  if (new Date(date.value) < new Date()) {
    toast.error(
      'Nie można wybrać tej daty, ponieważ jest to data przeszła. Wybierz datę z przyszłości',
    );
    date.value = null;
    dateRef.value?.focus();
    return;
  }
  if (herName.value === null) {
    toast.error('Nie wypełniono imienia Panny Młodej');
    herNameRef.value?.focus();
    return;
  }
  if (hisName.value === null) {
    toast.error('Nie wypełniono imienia Pana Młodego');
    hisNameRef.value?.focus();
    return;
  }

  if (modifyStore.getLinkData.googleToken && !fileToUpload.value) {
    toast.error('Nie wybrano zdjęcia do Waszej aplikacji');
    fileRef.value?.focus();
    return;
  }

  isFormSending.value = true;

  await modifyStore.trySetupData(modifyStore.linkId);
  if (modifyStore.getLinkData.bundleState !== IProductBundleState.NEW) {
    Logger.warn('Token date form was sended before');
    isFormSending.value = false;
    isFormSended.value = true;
    toast.error('Formularz został już przesłany.');
    return;
  }

  let isError = false;
  const id = (router.currentRoute.value.params['id'] as string) ?? '';
  try {
    let fileLink = '';
    if (modifyStore.getLinkData.googleToken) {
      const formData = new FormData();
      formData.append('file', fileToUpload.value as File);
      formData.append(
        'metadata',
        new Blob(
          [
            JSON.stringify({
              name: `${herName.value}_${hisName.value}_${date.value}_${id}`,
              parents: [`${googleDriveDirectory}`],
            }),
          ],
          { type: 'application/json' },
        ),
      );

      GoogleService.setAuthToken(modifyStore.getLinkData.googleToken);
      const fileId = await GoogleService.uploadFile(formData);
      fileLink = await GoogleService.getDownloadLink(fileId);
    }

    await ModifyLinkService.submitDateTokenInfo({
      id,
      date: new Date(date.value),
      herName: herName.value,
      hisName: hisName.value,
      isGallery: isGallery.value,
      paczkomatAddress: paczkomatAddress.value,
      isBundleToConnect: isBundleToConnect.value,
      fileLink,
    });

    const nextStep = await ModifyLinkService.getNextStep(id);
    if (nextStep.nextLinkId) {
      router.push({
        name: 'Link',
        params: {
          id: nextStep.nextLinkId,
        },
      });
    }
  } catch (err) {
    Logger.error('Error sending token date form', { err });
    toast.error(
      'Bląd podczas przesyłania formularza. Proszę spróbować ponownie.',
    );
    isError = true;
  }

  isFormSending.value = false;
  if (!isError) {
    isFormSended.value = true;
  }
}
</script>

<template>
  <div class="token-date">
    <h1 class="token-date-form-header">Witajcie</h1>
    <form class="token-date-form" @submit.prevent="submitTokenDateForm">
      <p class="token-date-form-p">
        Aby rozpocząć przygotowanie Waszej spersonalizowanej aplikacji weselnej,
        prosimy o podanie:
      </p>
      <ul class="token-date-form-list">
        <li class="token-date-form-list-element">
          <p class="token-date-form-list-element-p">Daty Waszego wesela</p>
          <input
            ref="dateRef"
            v-model="date"
            type="date"
            class="token-date-form-date"
          />
        </li>
        <li class="token-date-form-list-element">
          <p class="token-date-form-list-element-p">Imię Panny Młodej</p>
          <input
            ref="herNameRef"
            v-model="herName"
            type="text"
            class="token-date-form-text"
          />
        </li>
        <li class="token-date-form-list-element">
          <p class="token-date-form-list-element-p">Imię Pana Młodego</p>
          <input
            ref="hisNameRef"
            v-model="hisName"
            type="text"
            class="token-date-form-text"
          />
        </li>
      </ul>
      <ul class="token-date-form-list">
        <li
          v-if="modifyStore.getLinkData.isGalleryAvailable"
          class="token-date-form-list-element"
        >
          <p class="token-date-form-list-element-p">
            Czy galeria ma być widoczna również dla Waszych gości?
          </p>
          <label class="token-date-form-checkbox">
            <input
              v-model="isGallery"
              type="checkbox"
              class="token-date-form-checkbox-input"
            />
            <span class="token-date-form-checkbox-checkmark"></span>
          </label>
        </li>
        <li
          v-if="modifyStore.getLinkData.isShipmentAvailable"
          class="token-date-form-list-element"
        >
          <p class="token-date-form-list-element-p">
            Proszę wskazać paczkomat do wysyłki
          </p>
          <InpostGeowidgetElement
            v-model="paczkomatAddress"
          ></InpostGeowidgetElement>
        </li>
        <li
          v-if="modifyStore.getLinkData.googleToken"
          class="token-date-form-list-element"
        >
          <p class="token-date-form-list-element-p">
            Proszę podać zdjęcie które będzie w Waszej aplikacji
          </p>
          <input
            id="file-input"
            ref="fileRef"
            type="file"
            name="file-input"
            class="token-date-form-list-element-file-input"
            accept=".jpg, image/*, video/*, .heic"
            :disabled="isFormSending"
            @change="previewFile"
          />
          <label
            v-if="!fileToUpload"
            for="file-input"
            class="token-date-form-list-element-file-input-label token-date-form-list-element-file-input-label__plus"
            :class="{ disabled: isFormSending }"
          >
            <span class="token-date-form-list-element-file-input-label-plus"
              >+</span
            >
            <span class="token-date-form-list-element-file-input-label-text">
              Dodaj Wasze zdjęcie do aplikacji
            </span>
            <span class="token-date-form-list-element-file-input-label-sub-text"
              >Maksymalny rozmiar 25MB</span
            >
          </label>
          <label
            v-else
            for="file-input"
            class="token-date-form-list-element-file-input-label"
            :class="{ disabled: isFormSending }"
          >
            <span class="token-date-form-list-element-file-input-label-text">
              Wybrano {{ fileToUpload.name }}
            </span>
            <span class="token-date-form-list-element-file-input-label-text">
              Kliknij jeśli chcesz zmienić wybór
            </span>
            <span class="token-date-form-list-element-file-input-label-sub-text"
              >Maksymalny rozmiar 25MB</span
            >
          </label>
        </li>
      </ul>
      <p class="token-date-form-p">
        Te informacje pozwolą nam stworzyć unikalny kod do Waszej aplikacji
      </p>
      <button class="token-date-form-submit" type="submit">Wyślij</button>
    </form>
  </div>

  <transition name="fade">
    <SendingFormPopUp v-if="isFormSending"></SendingFormPopUp>
    <SendedFormPopUp v-else-if="isFormSended"></SendedFormPopUp>
  </transition>
</template>

<style lang="scss" scoped>
.fade-enter-active {
  transition: opacity 1.2s ease;
}

.fade-enter-from {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from,
.fade-leave-to {
  opacity: 0.8;
}

.token-date {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background: $white-100;
  color: $black;
  font-size: 16px;
  padding-bottom: 75px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-top: 50px;
  align-items: center;

  &-new_user {
    display: flex;
    flex-direction: column;
    gap: 25px;
    width: 90%;
    margin: 0 auto;

    &-p {
      text-align: center;
      font-size: 1.2em;
    }

    &-options {
      display: flex;
      flex-flow: row wrap;
      width: 100%;
      justify-content: space-evenly;

      &-button {
        font-size: 1.7em;
        font-weight: 700;
        border-radius: 14px;
        border: 2px solid $brown-200;
        padding: 5px 15px;
        cursor: pointer;
        transition: ease 0.7s all;
        background-color: $brown-200;
        color: $white-100;

        &:hover {
          background-color: $white-100;
          color: $brown-200;
        }
      }

      &-reversed {
        background-color: $white-100;
        color: $brown-200;

        &:hover {
          background-color: $brown-200;
          color: $white-100;
        }
      }
    }
  }

  &-form {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    flex-direction: column;
    width: 70%;

    &-header {
      font-size: 3.5em;
      line-height: 1.5em;
      font-weight: 500;
      color: $brown-200;
    }

    &-p {
      font-size: 1em;
      text-align: center;
    }

    &-text,
    &-date {
      border: 1px solid $black;
      border-radius: 15px;
      padding: 5px 10px;
      flex: 1 0 auto;
      background-color: $white-100;
      width: 100%;
      max-width: 350px;
    }

    &-checkbox {
      display: block;
      position: relative;
      padding-left: 25px;
      margin-bottom: 25px;
      cursor: pointer;
      font-size: 22px;
      user-select: none;

      &-input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
      }

      &-checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 25px;
        width: 25px;
        background-color: $white-100;
        border: 1px solid $black;
        border-radius: 5px;

        &::after {
          content: '';
          position: absolute;
          display: none;
          left: 9px;
          top: 5px;
          width: 5px;
          height: 10px;
          border: solid $white-100;
          border-width: 0 3px 3px 0;
          transform: rotate(45deg);
        }
      }

      &-input:checked ~ &-checkmark::after {
        display: block;
      }

      &-input:checked ~ &-checkmark {
        background-color: $brown-200;
      }

      &:hover &-input ~ &-checkmark {
        background-color: $brown-200;
      }
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: 15px;
      list-style-type: none;
      width: 100%;

      &-element {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        gap: 15px;

        &-p {
          font-size: 1.4em;
          font-weight: 700;
          color: $brown-200;
          text-align: center;
        }

        &-file-input {
          display: none;

          &-label {
            position: relative;
            width: 80%;
            display: flex;
            flex-direction: column;
            gap: 5px;
            cursor: pointer;
            border-radius: 15px;
            border: 2px solid $brown-200;
            padding: 12px 20px;
            margin-bottom: 15px;
            justify-content: center;
            align-items: center;

            @media screen and (max-width: $media-max-phone) {
              width: 100%;
            }

            &__plus {
              padding-left: 55px;
            }

            &-plus {
              position: absolute;
              left: 12px;
              color: $brown-200;
              text-align: center;
              font-size: 3em;
              line-height: 20px;
              border-radius: 50%;
              padding: 5px;
              border: 2px solid $brown-200;

              @media screen and (max-width: $media-max-phone) {
                font-size: 2.5em;
                line-height: 18px;
              }
            }

            &-text {
              display: flex;
              flex-direction: column;
              font-size: 20px;
              color: $brown-200;
              text-align: center;
              font-weight: 600;

              @media screen and (max-width: $media-max-phone) {
                font-size: 18px;
              }
            }

            &-sub-text {
              position: absolute;
              bottom: -20px;
              right: 0;
              color: $black;
              font-size: 10px;
            }
          }
        }
      }
    }

    &-submit {
      font-size: 1.5em;
      font-weight: 700;
      border-radius: 14px;
      border: 2px solid $brown-200;
      padding: 10px 25px;
      cursor: pointer;
      transition: ease 0.7s all;
      color: $brown-200;

      &:hover {
        background-color: $brown-200;
        color: $white-100;
      }
    }
  }
}
</style>
